import dayjs from 'dayjs';
import toPairs from 'lodash/toPairs';

import {
  COMMUNITY_INTERACTION_TYPES,
  COMMUNITY_ROUTE_NAMES,
  COMMUNITY_ROUTE_PRESET_FILTERS,
  DEFAULT_COMMUNITY_INTERACTION_SORT,
  INBOX_SORT_BY,
  UNREAD_QUERY_FILTER_KEYS,
} from '@/app/community/constants';
import { useIdentityStore } from '@/stores/identity';
import { useTrackingStore } from '@/stores/tracking';
import { applyReversibleUpdate } from '@/utils/optimism';

export const parseBoolean = (s) => {
  if (typeof s === 'boolean') {
    return s;
  }
  return ['true', 'false'].includes(s) ? s === 'true' : undefined;
};

export const getCommunityInteractionFilters = (query, ignoreAssigneeId = true) => {
  const identityStore = useIdentityStore();
  const { isArchived, isFollowUp, responseRecommended } = query;
  const assignment = ignoreAssigneeId ? {} : { assigneeId: identityStore?.identity?.id };
  return {
    isArchived: parseBoolean(isArchived),
    ...(responseRecommended !== true ? {} : { responseRecommended }),
    ...(isFollowUp === undefined ? {} : { isFollowUp: parseBoolean(isFollowUp) }),
    ...assignment,
  };
};

export const getCommunityInteractionSort = (query) =>
  query.sort || DEFAULT_COMMUNITY_INTERACTION_SORT;

export const getNumericIdsFromQuery = (query, key) => {
  const idQuery = query?.[key] ?? [];
  const ids = Array.isArray(idQuery) ? idQuery : idQuery.split(',');
  return ids.map((id) => Number(id));
};

export const sortCommunityInteractions = (communityInteractions, query) => {
  const sort = getCommunityInteractionSort(query);

  if (sort === INBOX_SORT_BY.RELEVANCE) {
    return communityInteractions;
  }

  let direction;

  if (sort === INBOX_SORT_BY.SRC_UPDATED_AT_DESC) {
    direction = -1;
  }

  if (sort === INBOX_SORT_BY.SRC_UPDATED_AT_ASC) {
    direction = 1;
  }

  return communityInteractions.sort((prev, next) => {
    const p1 = prev.sourceUpdatedAt ?? prev.createdAt;
    const p2 = next.sourceUpdatedAt ?? next.createdAt;
    if (direction === 1) {
      return p1 < p2 ? -1 : 1;
    }
    return p1 < p2 ? 1 : -1;
  });
};

export const filterCommunityInteractions = (
  communityInteractions,
  currentQuery,
  ignoreIsArchived = false,
  idFilters = [],
) => {
  const trackingStore = useTrackingStore();
  const routePresets = COMMUNITY_ROUTE_PRESET_FILTERS[trackingStore.currentRoute?.name] || {};
  const query = {};

  // Apply route presets first
  Object.keys(routePresets).forEach((key) => {
    query[key] = routePresets[key];
  });

  // Apply currentQuery, overriding presets with explicit values
  Object.keys(currentQuery).forEach((key) => {
    if (currentQuery[key] !== undefined) {
      query[key] = currentQuery[key];
    }
  });

  let tagOperator;
  let tagIds;
  if (query.tagIds) {
    [tagOperator, tagIds] = query.tagIds.split(':');
    tagIds = tagIds?.split(',').map((id) => Number(id)) || [];
  }
  // Ignore ADS_COMMENT type and use normal COMMENT type
  // since the ad comment is not its own interaction type, it's just a workaround to specify ad comment filter
  // *_ADS_COMMENT type is converted to *_COMMENT type + is_ad_comment = true in the backend
  const defaultTypes = query.subtype ? [] : Object.values(COMMUNITY_INTERACTION_TYPES);
  const interactionTypes = query.types?.replace(/_ADS/g, '') ?? defaultTypes;
  const brandIds = getNumericIdsFromQuery(query, 'brandIds');
  const filters = getCommunityInteractionFilters(
    query,
    trackingStore.currentRoute?.name !== COMMUNITY_ROUTE_NAMES.ASSIGNED_TO_YOU,
  );
  // Apply filters
  // didn't figure out the cause but communityInteractions can have {0:undefined} that causes issue
  // later on, so added an undefined filter guard in the end.
  let filtered = communityInteractions
    .filter((interaction) =>
      toPairs(filters).every(([key, value]) => {
        // temp work around for isArchived out of sync in DB and ES. Expose issue to UI to
        // let users have a chance to fix them instead of infinite loading
        if ((ignoreIsArchived || value === undefined) && key === 'isArchived') return true;
        return interaction[key] === value;
      }),
    )
    .filter((interaction) => interaction !== undefined);

  // Filter by tags
  if (tagIds?.length > 0) {
    const method = tagOperator === 'OR' ? 'some' : 'every';
    filtered = filtered.filter((interaction) =>
      tagIds[method]((tagId) => interaction.tagIds.includes(tagId)),
    );
  }

  // Filter by interaction type and subtype
  filtered = filtered.filter(
    (interaction) =>
      interactionTypes.includes(interaction.type) || query.subtype?.includes(interaction?.subtype),
  );

  // Filter by message content
  filtered = filtered.filter((interaction) => {
    const messageContentFilters = [];
    if (parseBoolean(query.isSpam)) messageContentFilters.push(interaction?.isSpam === true);
    if (parseBoolean(query.isQuestion))
      messageContentFilters.push(interaction?.sentiment?.isQuestion === true);
    if (query.messageContent) {
      messageContentFilters.push(
        query.messageContent.split(',').includes(interaction?.messageContent),
      );
    }
    return messageContentFilters.length === 0 || messageContentFilters.some(Boolean);
  });

  if (idFilters?.length > 0) {
    filtered = filtered.filter((interaction) => !idFilters.includes(interaction.id));
  }

  // Filter by date filters
  const { sourceUpdatedAfter: startDate, sourceUpdatedBefore: endDate } = query;
  if (startDate && endDate) {
    filtered = filtered.filter((interaction) => {
      let dateTime = interaction.sourceUpdatedAt ?? interaction.createdAt;
      // Below if is mainly a check for avoiding doing a large amount of test migrations
      if (!dayjs.isDayjs(dateTime)) {
        dateTime = dayjs(dateTime);
      }
      return dateTime.isBetween(
        dayjs(startDate).startOf('day'),
        dayjs(endDate).endOf('day'),
        'day',
        '[]',
      );
    });
  }
  // Filter by brands
  if (brandIds.length > 0) {
    filtered = filtered.filter((interaction) => brandIds.includes(interaction.brandId));
  }

  return filtered;
};

export const applyReversibleCommunityInteractionUpdate = (
  communityInteractions,
  id,
  { tagIds, ...flagUpdates },
) => {
  const communityInteraction = communityInteractions.find((ci) => ci.id === id);
  if (communityInteraction) {
    const addTagIds = tagIds?.add ?? [];
    const removeTagIds = tagIds?.remove ?? [];
    applyReversibleUpdate(communityInteractions, id, {
      ...flagUpdates,
      tagIds: communityInteraction.tagIds
        .filter((tid) => !removeTagIds.includes(tid) && !addTagIds.includes(tid))
        .concat(addTagIds),
    });
  }
};

export function getUnreadCountQueryParams(currentQuery) {
  return UNREAD_QUERY_FILTER_KEYS.reduce((queryParams, filterKey) => {
    // TODO: Remove brandId(s) checks once the multi brand inbox is fully rolled out
    if (filterKey === 'brandIds' && currentQuery.brandIds) {
      queryParams.brandIds = currentQuery.brandIds.split(',');
    } else {
      queryParams[filterKey] = currentQuery[filterKey];
    }
    return queryParams;
  }, {});
}
